import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { BANK_INFO } from "@/data/services";
import {
  Banknote, MessageCircle, Phone, Copy, Check, Lock, Globe, MapPin,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

/**
 * Public payment section.
 *
 * Bank/account data and the remittance details are gated behind authentication
 * so that anonymous visitors cannot scrape sensitive financial information —
 * the same gate applies to /api/payment-methods.
 *
 * The data shown adapts to the logged-in user's `country` field:
 *   • Mexican users      → BBVA CLABE + tarjeta + beneficiario.
 *   • Foreign users      → Remitly / Wise / Xoom / Ria / Western Union /
 *                          MoneyGram + datos para retiro en ventanilla
 *                          Soriana (México).
 */
export const PaymentSection = () => {
  const { t, lang } = useLanguage();
  const { user, isUser, isAdmin } = useAuth();
  const [copied, setCopied] = useState("");

  const copy = async (key, val) => {
    await navigator.clipboard.writeText(val);
    setCopied(key);
    toast.success(t("dialog.copied"));
    setTimeout(() => setCopied(""), 1200);
  };

  const steps = [
    t("payment.step1"),
    t("payment.step2"),
    t("payment.step3"),
    t("payment.step4"),
  ];

  // Resolve whether the logged-in user is Mexican (default: assume Mexican
  // when the field is empty so admins / staff don't lose access).
  const isMexican = useMemo(() => {
    if (!user) return null;
    if (isAdmin) return true;
    const c = (user.country || "").toLowerCase().trim();
    if (!c) return true;
    return ["méxico", "mexico", "mx"].some((tok) => c.includes(tok));
  }, [user, isAdmin]);

  const loggedIn = isUser || isAdmin;

  const bankRows = [
    { key: "bank", label: t("dialog.bank"), value: BANK_INFO.bank },
    { key: "clabe", label: t("dialog.clabe"), value: BANK_INFO.clabe },
    { key: "card", label: t("dialog.card"), value: BANK_INFO.card },
    { key: "beneficiary", label: t("dialog.beneficiary"), value: BANK_INFO.beneficiary },
  ];

  const intl = BANK_INFO.internationalRemittance || {};

  return (
    <section
      id="pago"
      className="relative py-24 lg:py-32 px-6 lg:px-12"
      data-testid="payment-section"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <div className="w-12 h-px bg-[#d4af37]/40" />
            <span className="uppercase tracking-[0.3em] text-xs">{t("nav.payment")}</span>
            <div className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white">
            {t("sections.paymentTitle")}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-dark rounded-2xl p-8 lg:p-10"
        >
          <h3 className="font-serif text-2xl text-white mb-6 flex items-center gap-2">
            <Banknote className="w-5 h-5 text-[#d4af37]" />
            {t("dialog.bankTransfer")}
          </h3>

          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-3" data-testid={`payment-step-${i + 1}`}>
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 text-[#d4af37] flex items-center justify-center font-serif text-base">
                  {i + 1}
                </span>
                <p className="text-sm text-neutral-300 pt-1.5 leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>

          <div className="divider-gold mb-8" />

          {/* ===================== AUTH GATE ===================== */}
          {!loggedIn ? (
            <div
              data-testid="payment-auth-gate"
              className="rounded-2xl border-2 border-[#d4af37]/40 bg-gradient-to-br from-[#d4af37]/10 via-purple-900/10 to-black p-6 sm:p-8 text-center"
            >
              <Lock className="w-10 h-10 text-[#d4af37] mx-auto mb-3" />
              <h4 className="font-serif text-xl text-white mb-2">
                {lang === "es" ? "Acceso para clientes" : "Client access"}
              </h4>
              <p className="text-sm text-neutral-300 mb-5 max-w-md mx-auto leading-relaxed">
                {lang === "es"
                  ? "Para proteger tu información y la nuestra, los datos bancarios y métodos de pago son visibles únicamente para usuarios registrados. Inicia sesión o crea tu cuenta — es gratis y solo toma un minuto."
                  : "To protect both our information and yours, bank details and payment methods are visible only to registered users. Log in or create an account — it's free and takes a minute."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/login"
                  data-testid="payment-login-cta"
                  className="btn-gold px-6 py-3 rounded-full text-xs uppercase tracking-widest"
                >
                  {lang === "es" ? "Iniciar sesión" : "Log in"}
                </Link>
                <Link
                  to="/registro"
                  data-testid="payment-register-cta"
                  className="btn-outline-gold px-6 py-3 rounded-full text-xs uppercase tracking-widest"
                >
                  {lang === "es" ? "Crear cuenta" : "Create account"}
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* ===================== MEXICAN USERS ===================== */}
              {isMexican && (
                <div data-testid="payment-mx-block">
                  <h4 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4">
                    {t("dialog.bankData")}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                    {bankRows.map((row) => (
                      <div
                        key={row.key}
                        className="flex items-center justify-between rounded-lg border border-white/10 bg-black/40 px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                            {row.label}
                          </div>
                          <div className="text-sm text-white font-mono truncate">{row.value}</div>
                        </div>
                        <button
                          data-testid={`payment-copy-${row.key}`}
                          onClick={() => copy(row.key, row.value)}
                          className="text-[#d4af37] hover:text-white text-xs flex items-center gap-1 ml-2"
                        >
                          {copied === row.key ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ===================== INTERNATIONAL USERS ===================== */}
              {!isMexican && (
                <div className="space-y-4" data-testid="payment-intl-block">
                  {/* BBVA México (international beneficiary) */}
                  <div className="rounded-2xl border-2 border-[#d4af37]/40 bg-gradient-to-br from-[#d4af37]/10 via-black to-black p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Banknote className="w-5 h-5 text-[#d4af37]" />
                      <h4 className="font-serif text-lg text-white">
                        {lang === "es" ? "Transferencia BBVA México" : "BBVA Mexico Bank Transfer"}
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[
                        { key: "intl-bank", label: t("dialog.bank"), value: BANK_INFO.internationalBank.bank },
                        { key: "intl-clabe", label: t("dialog.clabe"), value: BANK_INFO.internationalBank.clabe },
                        { key: "intl-card", label: t("dialog.card"), value: BANK_INFO.internationalBank.card },
                        { key: "intl-beneficiary", label: t("dialog.beneficiary"), value: BANK_INFO.internationalBank.beneficiary },
                      ].map((row) => (
                        <div
                          key={row.key}
                          className="flex items-center justify-between rounded-lg border border-white/10 bg-black/40 px-4 py-3"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{row.label}</div>
                            <div className="text-sm text-white font-mono truncate">{row.value}</div>
                          </div>
                          <button
                            data-testid={`payment-intl-bank-copy-${row.key}`}
                            onClick={() => copy(row.key, row.value)}
                            className="text-[#d4af37] hover:text-white text-xs flex items-center gap-1 ml-2"
                          >
                            {copied === row.key ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Remittance platforms */}
                  <div className="rounded-2xl border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/10 via-purple-900/10 to-black p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Globe className="w-5 h-5 text-sky-300" />
                      <h4 className="font-serif text-lg text-white">
                        {lang === "es" ? "Envío internacional de dinero" : "International money transfer"}
                      </h4>
                    </div>
                    <p className="text-sm text-neutral-300 mb-3">
                      {lang === "es"
                        ? "Usa cualquiera de estas plataformas para enviar tu pago desde fuera de México:"
                        : "Use any of these platforms to send your payment from outside Mexico:"}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                      {(intl.platforms || []).map((p) => (
                        <div
                          key={p}
                          data-testid={`payment-platform-${p.toLowerCase().replace(/\s+/g, "-")}`}
                          className="bg-black/40 border border-sky-500/30 rounded-lg px-3 py-2 text-center text-sm text-white"
                        >
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Soriana pickup */}
                  <div className="rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-black to-black p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-5 h-5 text-amber-300" />
                      <h4 className="font-serif text-lg text-white">
                        {lang === "es" ? `Retiro en ventanilla (${intl.pickupChain})` : `In-person pickup (${intl.pickupChain})`}
                      </h4>
                    </div>
                    <p className="text-sm text-neutral-300 mb-4">
                      {lang === "es"
                        ? `Si deseas que el dinero se retire en ventanilla, envía a la cadena "${intl.pickupChain}" (disponible en Western Union, MoneyGram, Ria) con estos datos:`
                        : `If you want in-person pickup, send to "${intl.pickupChain}" (available via Western Union, MoneyGram, Ria) using these details:`}
                    </p>
                    <div className="space-y-2">
                      {[
                        { key: "name", label: lang === "es" ? "Nombre completo" : "Full name", value: intl.pickupName },
                        { key: "state", label: lang === "es" ? "Estado / Ciudad" : "State / City", value: intl.pickupState },
                        { key: "phone", label: lang === "es" ? "Teléfono" : "Phone", value: intl.pickupPhone },
                      ].map((row) => (
                        <div
                          key={row.key}
                          className="flex items-center justify-between rounded-lg border border-white/10 bg-black/40 px-4 py-3"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{row.label}</div>
                            <div className="text-sm text-white font-mono truncate">{row.value}</div>
                          </div>
                          <button
                            data-testid={`payment-intl-copy-${row.key}`}
                            onClick={() => copy(row.key, row.value)}
                            className="text-amber-300 hover:text-white text-xs flex items-center gap-1 ml-2"
                          >
                            {copied === row.key ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-amber-300/80 mt-3">
                      {lang === "es"
                        ? "📲 Después del envío, envía la información del giro y el comprobante por WhatsApp."
                        : "📲 After sending, share the transfer details and receipt via WhatsApp."}
                    </p>
                  </div>
                </div>
              )}

              {/* ===================== PAYPAL (visible to all logged-in users) ===================== */}
              <div
                data-testid="payment-paypal-block"
                className="rounded-2xl border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/10 to-blue-500/5 p-5 sm:p-6 mt-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">💳</div>
                    <h4 className="font-serif text-lg text-white">
                      {lang === "es" ? "Pago por PayPal" : "Pay with PayPal"}
                    </h4>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-sky-300 bg-sky-500/15 border border-sky-500/40 rounded-full px-2 py-0.5">
                    {lang === "es" ? "Internacional" : "International"}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 bg-black/40 border border-white/10 rounded-lg px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {lang === "es" ? "Correo PayPal" : "PayPal Email"}
                    </div>
                    <div className="text-sm text-white font-mono truncate" data-testid="payment-paypal-email">
                      {BANK_INFO.paypalEmail}
                    </div>
                  </div>
                  <button
                    data-testid="payment-copy-paypal"
                    onClick={() => copy("paypal", BANK_INFO.paypalEmail)}
                    className="text-sky-300 hover:text-white text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-500/40 hover:bg-sky-500/10"
                  >
                    {copied === "paypal" ? <><Check className="w-3.5 h-3.5" /> {lang === "es" ? "Copiado" : "Copied"}</> : <><Copy className="w-3.5 h-3.5" /> {lang === "es" ? "Copiar" : "Copy"}</>}
                  </button>
                </div>
                <p className="text-[10px] text-neutral-400 mt-2">
                  {lang === "es"
                    ? "Envía el comprobante por WhatsApp después del pago."
                    : "Send the receipt via WhatsApp after payment."}
                </p>
              </div>

              {/* ============== WHATSAPP / CALL CTAs (logged-in users only) ============== */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                <a
                  data-testid="payment-whatsapp"
                  href={BANK_INFO.whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold w-full py-3.5 rounded-full flex items-center justify-center gap-2 uppercase text-xs tracking-widest"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp: {BANK_INFO.whatsapp}
                </a>
                <a
                  data-testid="payment-call"
                  href={`tel:${BANK_INFO.whatsapp.replace(/\s+/g, "")}`}
                  className="btn-outline-gold w-full py-3.5 rounded-full flex items-center justify-center gap-2 uppercase text-xs tracking-widest"
                >
                  <Phone className="w-4 h-4" />
                  {lang === "es" ? "Llamar Ahora" : "Call Now"}
                </a>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};
